import type { Goal, GoalPriority } from '../../api/types';

export interface GoalProgress {
  done: number;
  total: number;
  /** 0–100, and 0 for a goal with no tasks yet. */
  percent: number;
  /** Estimated cost of what is still open, in cents. */
  remainingCents: number;
  /** Estimated cost of everything on the list, in cents. */
  totalCents: number;
  /** Whether any task carries a price at all — no price, no totals to show. */
  hasPrices: boolean;
  /** Tasks with no price yet — left out of the totals. */
  unpriced: number;
}

export function goalProgress(goal: Goal): GoalProgress {
  const done = goal.tasks.filter((task) => task.status === 'completed').length;
  const total = goal.tasks.length;
  const priceOf = (task: Goal['tasks'][number]) => task.price_cents ?? 0;
  const totalCents = goal.tasks.reduce((sum, task) => sum + priceOf(task), 0);
  const remainingCents = goal.tasks
    .filter((task) => task.status !== 'completed')
    .reduce((sum, task) => sum + priceOf(task), 0);

  return {
    done,
    total,
    percent: total === 0 ? 0 : (done / total) * 100,
    remainingCents,
    totalCents,
    hasPrices: goal.tasks.some((task) => task.price_cents != null),
    unpriced: goal.tasks.filter((task) => task.price_cents == null).length,
  };
}

const PRIORITY_RANK: Record<GoalPriority, number> = { high: 0, medium: 1, low: 2 };

/** A car's open goals, most important first. */
export const activeGoals = (goals: Goal[] = []): Goal[] =>
  goals
    .filter((goal) => goal.status === 'active')
    .sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]);

/**
 * Rank of a car's most important open goal — cars with a high goal come
 * first on the garage page, cars with none last.
 */
export const urgencyRank = (goals: Goal[] = []): number => {
  const [top] = activeGoals(goals);
  return top ? PRIORITY_RANK[top.priority] : Object.keys(PRIORITY_RANK).length;
};
